export const CONTROL_JUMP_PARAM = 'control';
export const MAX_CONTROL_JUMP_MATCHES = 8;

export interface ControlJumpEntry {
  identifier: string;
  question: string;
  bundleId: string;
  bundleName: string;
}

// Navigation state that makes a repeat jump to the same control re-scroll.
export interface ControlJumpState {
  controlJumpAt: number;
}

interface JumpSourceBundle {
  _id: string;
  name: string;
  controls: readonly { identifier: string; question?: string | null }[];
}

const ENTITIES: Record<string, string> = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
};

export const toPlainText = (html: string): string =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(nbsp|amp|lt|gt|quot|#39);/g, (m) => ENTITIES[m])
    .replace(/\s+/g, ' ')
    .trim();

// An empty visibility map means every control is visible.
export const buildControlJumpEntries = (
  bundles: readonly JumpSourceBundle[],
  visibility: Map<string, Set<string>>
): ControlJumpEntry[] =>
  bundles.flatMap((bundle) => {
    const visible = visibility.get(bundle._id);
    return bundle.controls
      .filter(
        (c) => c.identifier.trim() && (!visible || visible.has(c.identifier))
      )
      .map((c) => ({
        identifier: c.identifier,
        question: toPlainText(c.question ?? ''),
        bundleId: bundle._id,
        bundleName: bundle.name,
      }));
  });

const matchRank = (entry: ControlJumpEntry, query: string): number => {
  const id = entry.identifier.toLowerCase();
  if (id === query) {
    return 0;
  }
  if (id.startsWith(query)) {
    return 1;
  }
  if (id.includes(query)) {
    return 2;
  }
  return entry.question.toLowerCase().includes(query) ? 3 : -1;
};

export const rankControlMatches = <T extends ControlJumpEntry>(
  entries: readonly T[],
  query: string,
  limit = MAX_CONTROL_JUMP_MATCHES
): T[] => {
  const q = query.trim().toLowerCase();
  if (!q) {
    return [];
  }
  return entries
    .map((entry, order) => ({ entry, order, rank: matchRank(entry, q) }))
    .filter((m) => m.rank >= 0)
    .sort((a, b) => a.rank - b.rank || a.order - b.order)
    .slice(0, limit)
    .map((m) => m.entry);
};

// A jump target that is in the list wins over the stepped index.
export const resolveFocusIndex = (
  identifiers: readonly string[],
  focusTarget: string | null,
  focusIndex: number
): number => {
  const targetIndex = focusTarget ? identifiers.indexOf(focusTarget) : -1;
  const index = targetIndex >= 0 ? targetIndex : focusIndex;
  return Math.max(0, Math.min(index, identifiers.length - 1));
};

export const controlJumpSearch = (identifier: string): string =>
  `?${CONTROL_JUMP_PARAM}=${encodeURIComponent(identifier)}`;
